import type { Placement } from '@floating-ui/dom';

/**
 * Marks the dropdown entry-animation wrapper. Its scale transform is neutralized
 * while measuring, otherwise every menu rect is reported at the mid-animation
 * size and the menu settles a few pixels off.
 */
export const DROPDOWN_ANIMATION_ATTRIBUTE = 'data-dropdown-animation';

/** Trigger subtree that holds the anchor glyph, excluding the caret button. */
const TRIGGER_GLYPH_ROOT_SELECTOR =
  '[data-testid="control-dropdown-anchor-icon"]';

/** Whole trigger box, including the caret, used when no glyph pair is available. */
const TRIGGER_BOX_SELECTORS = '[data-testid="control-dropdown-anchor"], button';

const MENU_ROW_SELECTORS =
  '[data-testid^="control-menu-dropdown-option-"], [data-testid^="visibility-menu-row-"]';

/** Every element the shared Icon component can render a glyph as. */
const GLYPH_SELECTORS = 'i.icon-font, modus-wc-icon, img, svg';

/** Glyphs drawn from the icon font, whose ink width is the font size. */
const ICON_FONT_SELECTOR = 'i.icon-font';

const CENTER_DIVISOR = 2;

/**
 * Inset kept free at the viewport edges. Must match the floating-ui `shift`
 * padding in useFloatingMenuPosition, otherwise the glyph nudge below and that
 * middleware would clamp the panel to two different edges.
 */
export const DROPDOWN_MENU_VIEWPORT_PADDING = 8;

const resolveTriggerGlyph = (anchor: HTMLElement): HTMLElement | null => {
  const glyphRoot =
    anchor.querySelector<HTMLElement>(TRIGGER_GLYPH_ROOT_SELECTOR) ??
    anchor.querySelector<HTMLElement>('button');
  return glyphRoot?.querySelector<HTMLElement>(GLYPH_SELECTORS) ?? null;
};

const numericStyle = (styles: CSSStyleDeclaration, property: string): number =>
  parseFloat(styles.getPropertyValue(property)) || 0;

/**
 * Left edge of the glyph a user actually sees. Icon-font glyphs are drawn at the
 * font size and centered on the content box, so they overflow a box that has been
 * narrowed by padding; other glyph elements simply fill their content box. Using
 * the ink edge keeps two icons of different sizes visually left-aligned.
 */
const glyphInkLeft = (el: HTMLElement): number => {
  const rect = el.getBoundingClientRect();
  const styles = getComputedStyle(el);
  const paddingLeft = numericStyle(styles, 'padding-left');
  const paddingRight = numericStyle(styles, 'padding-right');
  const contentWidth = rect.width - paddingLeft - paddingRight;
  const inkWidth = el.matches(ICON_FONT_SELECTOR)
    ? numericStyle(styles, 'font-size')
    : contentWidth;
  return rect.left + paddingLeft + (contentWidth - inkWidth) / CENTER_DIVISOR;
};

const clampMenuXToViewport = (x: number, menuWidth: number): number => {
  const viewportWidth =
    document.documentElement.clientWidth || window.innerWidth;
  const minX = DROPDOWN_MENU_VIEWPORT_PADDING;
  const maxX = viewportWidth - menuWidth - DROPDOWN_MENU_VIEWPORT_PADDING;
  // A menu wider than the viewport cannot satisfy both edges; keep the leading one.
  if (maxX < minX) return minX;
  return Math.min(Math.max(x, minX), maxX);
};

const resolveDeltaX = (
  anchor: HTMLElement,
  menuRow: HTMLElement,
  alignEnd: boolean
): number | null => {
  const triggerGlyph = resolveTriggerGlyph(anchor);
  const rowGlyph = menuRow.querySelector<HTMLElement>(GLYPH_SELECTORS);
  if (triggerGlyph && rowGlyph) {
    return glyphInkLeft(triggerGlyph) - glyphInkLeft(rowGlyph);
  }
  const triggerBox = anchor.querySelector<HTMLElement>(TRIGGER_BOX_SELECTORS);
  if (!triggerBox) return null;
  const triggerRect = triggerBox.getBoundingClientRect();
  const rowRect = menuRow.getBoundingClientRect();
  return alignEnd
    ? triggerRect.right - rowRect.right
    : triggerRect.left - rowRect.left;
};

const measureSettled = <T>(menu: HTMLElement, measure: () => T): T => {
  const animated = menu.querySelector<HTMLElement>(
    `[${DROPDOWN_ANIMATION_ATTRIBUTE}]`
  );
  if (!animated) return measure();
  const inlineTransform = animated.style.transform;
  animated.style.transform = 'none';
  try {
    return measure();
  } finally {
    animated.style.transform = inlineTransform;
  }
};

/**
 * After floating-ui positions the menu panel against the anchor, nudge horizontally
 * so interactive content lines up with the trigger — not just the outer panel edges.
 * Works for any anchor position in a toolbar (left, middle, right).
 *
 * When both the trigger and the first row have a glyph, line up those icon
 * columns. That is the leading edge a user reads, including split dropdowns on
 * the right of a toolbar. End placements without a row glyph line up the boxes
 * instead, since those rows have no ink on the right side.
 *
 * The nudge runs after floating-ui. Clamp x to the viewport so an end-placement
 * glyph shift cannot push a wide menu past the window edge.
 */
export const alignDropdownMenuContentWithTrigger = (options: {
  anchor: HTMLElement;
  menu: HTMLElement;
  x: number;
  y: number;
  placement: Placement;
}): { x: number; y: number } => {
  options.menu.style.top = `${options.y}px`;
  options.menu.style.left = `${options.x}px`;

  const menuRow = options.menu.querySelector<HTMLElement>(MENU_ROW_SELECTORS);
  if (!menuRow) return { x: options.x, y: options.y };

  const alignEnd = options.placement.endsWith('end');

  const measured = measureSettled(options.menu, () => ({
    deltaX: resolveDeltaX(options.anchor, menuRow, alignEnd),
    menuWidth: options.menu.getBoundingClientRect().width,
  }));

  if (measured.deltaX === null) return { x: options.x, y: options.y };
  return {
    x: clampMenuXToViewport(options.x + measured.deltaX, measured.menuWidth),
    y: options.y,
  };
};
